import { useState } from "react";
import { C, PageHeader, GlassCard, Sparkline, Skeleton, Stat } from "@coremonit/design-system";
import { Activity } from "lucide-react";
import { useServiceMetrics, useServices } from "../api/hooks";
import type { MetricSeries } from "@coremonit/types";

export function MetricsPage() {
  const services = useServices();
  const [serviceId, setServiceId] = useState<string | undefined>();
  const metrics = useServiceMetrics(serviceId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader icon={<Activity size={16} />} title="Metrics" subtitle="Live · P50/P90/P95/P99 · GC · heap · threads · cache · queue" />
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ color: C.textMuted, fontSize: 11 }}>Service</span>
          <select
            value={serviceId ?? ""}
            onChange={(e) => setServiceId(e.target.value || undefined)}
            style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "6px 10px", fontSize: 12, minWidth: 260 }}
          >
            <option value="">Select a service…</option>
            {services.data?.items.map((s) => <option key={s.id} value={s.id} style={{ background: C.surface }}>{s.name}</option>)}
          </select>
        </div>
      </GlassCard>

      {!serviceId ? (
        <GlassCard style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 12 }}>
          Choose a service to load its metrics.
        </GlassCard>
      ) : metrics.isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {Array.from({ length: 12 }, (_, i) => <Skeleton key={i} height={120} radius={14} />)}
        </div>
      ) : metrics.data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
            <Stat label="P50 latency" value={`${last(metrics.data.latencyP50)}ms`} color={C.green} />
            <Stat label="P90 latency" value={`${last(metrics.data.latencyP90)}ms`} color={C.blue} />
            <Stat label="P95 latency" value={`${last(metrics.data.latencyP95)}ms`} color={C.amber} />
            <Stat label="P99 latency" value={`${last(metrics.data.latencyP99)}ms`} color={C.red} />
            <Stat label="Availability" value={`${last(metrics.data.availability).toFixed(2)}%`} color={C.green} />
            <Stat label="Error rate"   value={`${last(metrics.data.errorRate).toFixed(2)}%`} color={C.red} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
            <ChartCard title="CPU"           series={metrics.data.cpu}         color={C.blue}   suffix="%" />
            <ChartCard title="Memory"        series={metrics.data.memory}      color={C.purple} suffix="%" />
            <ChartCard title="Disk"          series={metrics.data.disk}        color={C.cyan}   suffix="%" />
            <ChartCard title="Network"       series={metrics.data.network}     color={C.green}  suffix="Mbps" />
            <ChartCard title="IOPS"          series={metrics.data.iops}        color={C.amber}  />
            <ChartCard title="Request rate"  series={metrics.data.requestRate} color={C.blue}   suffix="rpm" />
            <ChartCard title="GC"            series={metrics.data.gcPct}       color={C.pink}   suffix="%" />
            <ChartCard title="Heap"          series={metrics.data.heapMb}      color={C.purple} suffix="MB" />
            <ChartCard title="Threads"       series={metrics.data.threads}     color={C.cyan}   />
            <ChartCard title="Connections"   series={metrics.data.connections} color={C.blue}   />
            <ChartCard title="Queue length"  series={metrics.data.queueLength} color={C.amber}  />
            <ChartCard title="Cache hit"     series={metrics.data.cacheHitRate}color={C.green}  suffix="%" />
          </div>
        </>
      )}
    </div>
  );
}

const last = (s: MetricSeries) => s.points[s.points.length - 1]?.value ?? 0;

function ChartCard({ title, series, color, suffix }: { title: string; series: MetricSeries; color: string; suffix?: string }) {
  const data = series.points.map((p) => p.value);
  const cur = last(series);
  return (
    <GlassCard style={{ padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{title}</div>
        <div style={{ color, fontSize: 13, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{cur.toLocaleString()}{suffix ?? ""}</div>
      </div>
      <Sparkline data={data} color={color} width={240} height={60} />
    </GlassCard>
  );
}
